import { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'nessenius_site_images';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Default/fallback images
const DEFAULT_IMAGES = {
  // Homepage
  home_hero: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=1920',
  // About Page
  about_team: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800',
  // Services - all 5 services
  service_gartenpflege: 'https://images.unsplash.com/photo-1683316924890-6a8c5ab10d29?w=800',
  service_baumfaellung: 'https://images.unsplash.com/photo-1626828476637-5bd713ef9f22?w=800',
  service_baggerarbeiten: 'https://images.unsplash.com/photo-1622082679766-c5912d9416eb?w=800',
  service_entsorgung: 'https://images.unsplash.com/photo-1574359173269-291f060e6fe1?w=800',
  service_hochdruckreinigung: 'https://images.unsplash.com/photo-1718152470408-cfeebeb6b9fc?w=800',
};

// Legacy key mapping for backwards compatibility with existing DB data
const LEGACY_KEY_MAP = {
  'service_garden': 'service_gartenpflege',
  'service_tree': 'service_baumfaellung',
  'service_earth': 'service_baggerarbeiten',
  'service_cleaning': 'service_hochdruckreinigung',
};

// Reverse mapping to find legacy keys
const NEW_TO_LEGACY_MAP = {
  'service_gartenpflege': 'service_garden',
  'service_baumfaellung': 'service_tree',
  'service_baggerarbeiten': 'service_earth',
  'service_hochdruckreinigung': 'service_cleaning',
};

// Convert relative URL to absolute URL
const formatImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Get cached images from localStorage - synchronous, called once at module load
const getCachedImages = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { images, timestamp } = JSON.parse(cached);
      // Check if cache is still valid
      if (Date.now() - timestamp < CACHE_DURATION) {
        return images;
      }
    }
  } catch (e) {
    console.error('Error reading image cache:', e);
  }
  return null;
};

// Save images to localStorage cache
const setCachedImages = (images) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      images,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error('Error saving image cache:', e);
  }
};

// Pre-load cached images at module initialization to avoid flicker
const INITIAL_CACHED_IMAGES = getCachedImages();

export const useSiteImages = () => {
  // Initialize with cached images immediately - no loading state if cache exists
  const [images, setImages] = useState(INITIAL_CACHED_IMAGES || DEFAULT_IMAGES);
  const [loading, setLoading] = useState(false); // Start false - never show loading state
  const hasFetched = useRef(false);

  useEffect(() => {
    // Only fetch once per session
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchImages = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/site-images`);
        if (response.ok) {
          const data = await response.json();
          // Merge with defaults - database images override defaults
          const imageMap = { ...DEFAULT_IMAGES };
          data.images.forEach(img => {
            if (img.url) {
              const formattedUrl = formatImageUrl(img.url);
              // Store under original key
              imageMap[img.key] = formattedUrl;
              // Also map legacy keys to new keys
              if (LEGACY_KEY_MAP[img.key]) {
                imageMap[LEGACY_KEY_MAP[img.key]] = formattedUrl;
              }
            }
          });
          setImages(imageMap);
          // Cache the images for next page load
          setCachedImages(imageMap);
        }
      } catch (error) {
        console.error('Error fetching site images:', error);
        // Keep cached/default images on error
      }
    };

    fetchImages();
  }, []);

  const getImage = (key) => {
    // Check direct key first
    if (images[key]) return images[key];
    // Check if there's a legacy key that might have data
    const legacyKey = NEW_TO_LEGACY_MAP[key];
    if (legacyKey && images[legacyKey]) return images[legacyKey];
    // Return default
    return DEFAULT_IMAGES[key] || '';
  };

  return { images, loading, getImage };
};

export default useSiteImages;
